import { useCallback, useEffect, useRef, useState } from 'react';
import pipelineService, { PipelineRun } from '../services/pipelineService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';

export interface FeedItem {
  seq?: number;
  type: string;            // stage | telemetry | gate | run
  stage?: string | null;
  stream?: string | null;
  status?: string | null;
  message?: string | null;
  metrics?: any;
  ts?: string;
}

interface RunEvents {
  run: PipelineRun | null;
  feed: FeedItem[];
  connected: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

const TERMINAL = new Set(['succeeded', 'failed', 'cancelled']);

/** Live run state via a fetch-based SSE stream (EventSource can't send the Bearer header). */
export function useRunEvents(runId?: string): RunEvents {
  const [run, setRun] = useState<PipelineRun | null>(null);
  const [feed, setFeed] = useState<FeedItem[]>([]);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seen = useRef<Set<number>>(new Set());

  const reload = useCallback(async () => {
    if (!runId) return;
    try {
      const { run } = await pipelineService.getRun(runId);
      setRun(run);
    } catch {
      /* ignore transient */
    }
  }, [runId]);

  useEffect(() => {
    if (!runId) return;
    const ctrl = new AbortController();
    seen.current = new Set();
    setFeed([]);
    setError(null);

    const patchStage = (r: PipelineRun, ev: FeedItem): PipelineRun => {
      const stages = r.stages.map((s) =>
        s.stage === ev.stage && (ev.stream == null || s.stream === ev.stream)
          ? { ...s, status: ev.status || s.status, metrics: ev.metrics || s.metrics }
          : s);
      return { ...r, stages };
    };

    const handle = (frame: string) => {
      let type = 'message';
      let data: any = null;
      for (const line of frame.split('\n')) {
        if (line.startsWith('event:')) type = line.slice(6).trim();
        else if (line.startsWith('data:')) {
          try { data = JSON.parse(line.slice(5).trim()); } catch { /* keepalive */ }
        }
      }
      if (!data) return;
      if (type === 'snapshot') { setRun(data.run); return; }
      if (data.seq && seen.current.has(data.seq)) return;
      if (data.seq) seen.current.add(data.seq);

      setFeed((f) => [...f, { type, ...data }]);
      if (type === 'stage') setRun((r) => (r ? patchStage(r, data) : r));
      if (type === 'run') {
        setRun((r) => (r ? { ...r, status: data.status || r.status } : r));
        reload();  // fetch authoritative full state (fields, gate payload, eval)
      }
      if (type === 'gate') reload();
    };

    (async () => {
      try {
        const token = localStorage.getItem('access_token');
        const resp = await fetch(`${API_URL}/pipeline/runs/${runId}/events`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: ctrl.signal,
        });
        if (!resp.ok || !resp.body) { setError('Live stream unavailable'); return; }
        setConnected(true);
        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const frames = buf.split('\n\n');
          buf = frames.pop() || '';
          frames.forEach(handle);
        }
      } catch (e) {
        if (!ctrl.signal.aborted) setError('Live stream disconnected');
      } finally {
        setConnected(false);
      }
    })();

    return () => ctrl.abort();
  }, [runId, reload]);

  return { run, feed, connected, error, reload };
}

export { TERMINAL };
